import type { EpgInfoDto, TimeRange, TvEpgApiDto } from '@setplex/tria-api'
import type { ICombinedPlayerTvData } from '~/entities/player/tv/index.h'
import type { CurrentTvMediaUrlWithEpg } from '~/shared/interfaces'

import { MIN_PROGRAM_LENGTH_MS, ScheduleType } from '@setplex/tria-api'
import { endOfHour, formatISO, getUnixTime, parseISO, subHours } from 'date-fns'

const MAX_REWIND_BACK_HOURS = 2

export const generateEmptyProgramForNow = (): EpgInfoDto => ({
  start: formatISO(subHours(new Date(), MAX_REWIND_BACK_HOURS)).toString(), // it replaced later with 'rewindLengthSec'
  stop: formatISO(endOfHour(new Date())).toString(),
  title: '',
  description: '',
  subtitle: '',
  categories: [],
  icon: '',
  date: '',
  rating: '',
  directors: [],
  actors: [],
  episode: '',
})

// MIN_PROGRAM_LENGTH_MS window — shorter can return [] due to gaps; this range may still return two programs
export const getCurrentEpgPeriod = (): TimeRange => {
  const now = Date.now()
  return {
    start: now - MIN_PROGRAM_LENGTH_MS,
    end: now,
  }
}

export const getCurrentProgram = ({
  epg,
}: {
  epg: TvEpgApiDto
  period: TimeRange
}): EpgInfoDto => {
  const epgId = Object.keys(epg)[0] // should be only one channel / epgId
  const programs = epg[epgId] ?? []

  if (programs.length === 0) return generateEmptyProgramForNow()
  if (programs.length === 1) return programs[0]

  const now = Date.now()
  const airingNow = programs.find((program) => {
    const start = new Date(program.start).getTime()
    const stop = new Date(program.stop).getTime()

    return start <= now && now < stop
  })

  if (airingNow) return airingNow

  // take the latest program that already started
  const sorted = programs
    .filter((program) => new Date(program.start).getTime() <= now)
    .sort((a, b) => new Date(b.start).getTime() - new Date(a.start).getTime())

  if (sorted.length > 0) return sorted[0]

  return programs[0]
}

export const getStartEndWithRewindLength = ({
  rewindLengthSec,
  program,
}: {
  rewindLengthSec?: number | null
  program: EpgInfoDto
}): { startTime: number; endTime: number } => {
  const hasEpg = Boolean(program.title)

  const rewindLength = rewindLengthSec
    ? rewindLengthSec / 3600
    : MAX_REWIND_BACK_HOURS

  const fakeStartTime = formatISO(subHours(new Date(), rewindLength)).toString()
  const fakeEndTime = formatISO(endOfHour(new Date())).toString()

  const startTime = getUnixTime(
    parseISO(hasEpg ? program.start : fakeStartTime)
  )
  const endTime = getUnixTime(parseISO(hasEpg ? program.stop : fakeEndTime))

  return { startTime, endTime }
}

export const mergeUrlDataAndProgram = ({
  mediaUrlData,
  program,
  channel,
}: ICombinedPlayerTvData): CurrentTvMediaUrlWithEpg => {
  const hasEpg = Boolean(program.title)

  const rewindLengthSec = mediaUrlData.liveRewindOptions?.rewindLengthSec

  const { startTime, endTime } = getStartEndWithRewindLength({
    rewindLengthSec,
    program,
  })

  return {
    ...mediaUrlData,
    programTitle: program.title,
    scheduleType: hasEpg ? ScheduleType.EPG : ScheduleType.HOURLY,
    startTime,
    endTime,
    channelId: channel.id,
  }
}
